/**
 * SPEC-20260507-0136 (Fase 3): editor de step `http.invoke`.
 *
 * Renderizado pelo SagaDefinitionEditor quando step.type === 'http.invoke'.
 * Edita `step.inputMapping.fields` com prefixos:
 * - `literal:<text>`              → valor estático (URL fixo, método, etc)
 * - `literal-json:<json>`         → valor JSON estruturado (headers, body, expectStatus)
 * - `input.<path>` / `step[N].output.<path>` → resolvido em runtime
 *
 * UI: cada campo tem layout vertical limpo — label em cima, [seletor de modo
 * compacto + value field] em linha embaixo, hint depois. Cada campo declara
 * QUAIS modos são válidos pra ele (method não aceita literal-json, headers
 * sempre é literal-json ou path, etc).
 */

import { useMemo } from "react";
import { AlertTriangle } from "lucide-react";
import type { SagaStepDefinition, InputMapping } from "../../types/saga-definition";
import { Label } from "../ui/label";
import { cn } from "../../utils/cn";

const HTTP_METHODS = ["GET", "POST", "PUT", "PATCH", "DELETE"] as const;
const COMPENSATE_METHODS = ["POST", "DELETE"] as const;

export interface HttpInvokeStepEditorProps {
  step: SagaStepDefinition;
  onUpdate(patch: Partial<SagaStepDefinition>): void;
  readOnly?: boolean;
}

type SourceMode = "literal" | "literal-json" | "path";

interface ParsedSource {
  mode: SourceMode;
  value: string;
}

const MODE_LABELS: Record<SourceMode, string> = {
  literal: "literal",
  "literal-json": "JSON",
  path: "path",
};

function parseSource(raw: string | undefined, defaultMode: SourceMode): ParsedSource {
  if (!raw) return { mode: defaultMode, value: "" };
  if (raw.startsWith("literal-json:")) return { mode: "literal-json", value: raw.slice("literal-json:".length) };
  if (raw.startsWith("literal:")) return { mode: "literal", value: raw.slice("literal:".length) };
  return { mode: "path", value: raw };
}

function serializeSource(parsed: ParsedSource): string {
  if (!parsed.value) return "";
  if (parsed.mode === "literal") return `literal:${parsed.value}`;
  if (parsed.mode === "literal-json") return `literal-json:${parsed.value}`;
  return parsed.value;
}

export function HttpInvokeStepEditor({ step, onUpdate, readOnly = false }: HttpInvokeStepEditorProps) {
  const fields = step.inputMapping?.fields ?? {};

  function setField(key: string, raw: string) {
    const next: Record<string, string> = { ...fields };
    if (!raw) delete next[key];
    else next[key] = raw;
    const mapping: InputMapping | undefined =
      Object.keys(next).length > 0 ? { fields: next } : undefined;
    onUpdate({ inputMapping: mapping });
  }

  const method = useMemo(() => parseSource(fields.method, "literal"), [fields.method]);
  const url = useMemo(() => parseSource(fields.url, "literal"), [fields.url]);
  const headers = useMemo(() => parseSource(fields.headers, "literal-json"), [fields.headers]);
  const body = useMemo(() => parseSource(fields.body, "literal-json"), [fields.body]);
  const expectStatus = useMemo(() => parseSource(fields.expectStatus, "literal-json"), [fields.expectStatus]);
  const timeoutMs = useMemo(() => parseSource(fields.timeoutMs, "literal"), [fields.timeoutMs]);
  const compensateUrl = useMemo(() => parseSource(fields.compensateUrl, "literal"), [fields.compensateUrl]);
  const compensateMethod = useMemo(() => parseSource(fields.compensateMethod, "literal"), [fields.compensateMethod]);

  return (
    <div className="space-y-3 pt-2 border-t border-border-subtle">
      <div className="rounded border border-info/30 bg-info-soft/30 p-2 text-xs flex items-start gap-2">
        <AlertTriangle size={13} className="text-info shrink-0 mt-0.5" />
        <span className="text-info-soft-fg leading-relaxed">
          <code className="font-mono-tight">http.invoke</code> chama uma URL externa em runtime.
          Cada campo é <strong>literal</strong> (valor fixo na definition), <strong>JSON</strong>{" "}
          (objeto/array literal) ou <strong>path</strong> (resolve de{" "}
          <code className="font-mono-tight">input.X</code> ou{" "}
          <code className="font-mono-tight">step[N].output.X</code>).
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3">
        <Field
          label="Método"
          required
          help={method.mode === "literal" ? "Verbo HTTP" : "Path resolve em runtime"}
        >
          <Row>
            <ModeSelect
              value={method.mode}
              modes={["literal", "path"]}
              onChange={(m) => setField("method", serializeSource({ mode: m, value: method.value }))}
              disabled={readOnly}
            />
            {method.mode === "literal" ? (
              <select
                disabled={readOnly}
                value={method.value}
                onChange={(e) => setField("method", serializeSource({ mode: "literal", value: e.target.value }))}
                className={inputCls}
              >
                <option value="">— selecione —</option>
                {HTTP_METHODS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            ) : (
              <input
                disabled={readOnly}
                type="text"
                value={method.value}
                onChange={(e) => setField("method", serializeSource({ mode: "path", value: e.target.value }))}
                placeholder="input.requestMethod"
                className={inputCls}
              />
            )}
          </Row>
        </Field>

        <Field
          label="URL"
          required
          help="URL completa (literal) ou path do input/step (ex: input.targetUrl)"
        >
          <Row>
            <ModeSelect
              value={url.mode}
              modes={["literal", "path"]}
              onChange={(m) => setField("url", serializeSource({ mode: m, value: url.value }))}
              disabled={readOnly}
            />
            <input
              disabled={readOnly}
              type="text"
              value={url.value}
              onChange={(e) => setField("url", serializeSource({ mode: url.mode, value: e.target.value }))}
              placeholder={url.mode === "path" ? "input.targetUrl" : "https://api.example.com/foo"}
              className={inputCls}
            />
          </Row>
        </Field>
      </div>

      <Field
        label="Headers"
        help="Objeto JSON (Content-Type: application/json é setado automaticamente)"
      >
        <Row align="start">
          <ModeSelect
            value={headers.mode}
            modes={["literal-json", "path"]}
            onChange={(m) => setField("headers", serializeSource({ mode: m, value: headers.value }))}
            disabled={readOnly}
          />
          <textarea
            disabled={readOnly}
            value={headers.value}
            onChange={(e) => setField("headers", serializeSource({ mode: headers.mode, value: e.target.value }))}
            placeholder={headers.mode === "path" ? "input.requestHeaders" : '{"Authorization":"Bearer ...","X-Foo":"bar"}'}
            rows={2}
            spellCheck={false}
            className={cn(inputCls, "font-mono-tight resize-y")}
          />
        </Row>
      </Field>

      <Field
        label="Body"
        help="Objeto JSON (literal) ou path (ex: input.payload). Vazio = sem body."
      >
        <Row align="start">
          <ModeSelect
            value={body.mode}
            modes={["literal-json", "path"]}
            onChange={(m) => setField("body", serializeSource({ mode: m, value: body.value }))}
            disabled={readOnly}
          />
          <textarea
            disabled={readOnly}
            value={body.value}
            onChange={(e) => setField("body", serializeSource({ mode: body.mode, value: e.target.value }))}
            placeholder={body.mode === "path" ? "input.payload" : '{"tenantKey":"acme"}'}
            rows={3}
            spellCheck={false}
            className={cn(inputCls, "font-mono-tight resize-y")}
          />
        </Row>
      </Field>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Field
          label="Expect status"
          help="JSON array de status codes aceitos. Default: [200,201,202,204]."
        >
          <Row>
            <ModeSelect
              value={expectStatus.mode}
              modes={["literal-json", "path"]}
              onChange={(m) => setField("expectStatus", serializeSource({ mode: m, value: expectStatus.value }))}
              disabled={readOnly}
            />
            <input
              disabled={readOnly}
              type="text"
              value={expectStatus.value}
              onChange={(e) => setField("expectStatus", serializeSource({ mode: expectStatus.mode, value: e.target.value }))}
              placeholder="[200,201]"
              className={cn(inputCls, "font-mono-tight")}
            />
          </Row>
        </Field>

        <Field label="Timeout (ms)" help="Default: 30000 (30s).">
          <Row>
            <ModeSelect
              value={timeoutMs.mode}
              modes={["literal", "path"]}
              onChange={(m) => setField("timeoutMs", serializeSource({ mode: m, value: timeoutMs.value }))}
              disabled={readOnly}
            />
            <input
              disabled={readOnly}
              type={timeoutMs.mode === "literal" ? "number" : "text"}
              value={timeoutMs.value}
              onChange={(e) => setField("timeoutMs", serializeSource({ mode: timeoutMs.mode, value: e.target.value }))}
              placeholder={timeoutMs.mode === "path" ? "input.timeoutMs" : "30000"}
              className={inputCls}
            />
          </Row>
        </Field>
      </div>

      <details className="rounded border border-border-subtle bg-surface-subtle/30">
        <summary className="cursor-pointer px-2.5 py-2 text-[10px] uppercase tracking-wider text-muted-foreground font-medium hover:bg-surface-2 rounded">
          Compensation (rollback HTTP) — opcional
        </summary>
        <div className="p-2.5 pt-1 grid grid-cols-1 md:grid-cols-[180px_1fr] gap-3">
          <Field label="Compensate method">
            <Row>
              <ModeSelect
                value={compensateMethod.mode}
                modes={["literal", "path"]}
                onChange={(m) => setField("compensateMethod", serializeSource({ mode: m, value: compensateMethod.value }))}
                disabled={readOnly}
              />
              {compensateMethod.mode === "literal" ? (
                <select
                  disabled={readOnly}
                  value={compensateMethod.value}
                  onChange={(e) => setField("compensateMethod", serializeSource({ mode: "literal", value: e.target.value }))}
                  className={inputCls}
                >
                  <option value="">— selecione —</option>
                  {COMPENSATE_METHODS.map((m) => (
                    <option key={m} value={m}>{m}</option>
                  ))}
                </select>
              ) : (
                <input
                  disabled={readOnly}
                  type="text"
                  value={compensateMethod.value}
                  onChange={(e) => setField("compensateMethod", serializeSource({ mode: "path", value: e.target.value }))}
                  placeholder="input.compensateMethod"
                  className={inputCls}
                />
              )}
            </Row>
          </Field>

          <Field label="Compensate URL">
            <Row>
              <ModeSelect
                value={compensateUrl.mode}
                modes={["literal", "path"]}
                onChange={(m) => setField("compensateUrl", serializeSource({ mode: m, value: compensateUrl.value }))}
                disabled={readOnly}
              />
              <input
                disabled={readOnly}
                type="text"
                value={compensateUrl.value}
                onChange={(e) => setField("compensateUrl", serializeSource({ mode: compensateUrl.mode, value: e.target.value }))}
                placeholder={compensateUrl.mode === "path" ? "step[0].output.compensateUrl" : "https://api.example.com/foo/{id}"}
                className={inputCls}
              />
            </Row>
          </Field>
        </div>
        <p className="px-2.5 pb-2 text-[10px] text-muted-foreground">
          Quando declarados, em failure da saga este step chama{" "}
          <code className="font-mono-tight">compensateMethod compensateUrl</code> pra desfazer.
        </p>
      </details>
    </div>
  );
}

// ─── Sub-componentes (presentational) ────────────────────────────────────────

const inputCls =
  "flex-1 min-w-0 px-2 py-1.5 text-xs border border-border rounded bg-bg disabled:bg-surface-subtle disabled:text-muted-foreground";

interface FieldProps {
  label: string;
  required?: boolean;
  help?: string;
  children: React.ReactNode;
}

function Field({ label, required, help, children }: FieldProps) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium block mb-1">
        {label} {required && <span className="text-danger">*</span>}
      </Label>
      {children}
      {help && <p className="text-[10px] text-muted-foreground mt-0.5">{help}</p>}
    </div>
  );
}

function Row({ children, align = "center" }: { children: React.ReactNode; align?: "center" | "start" }) {
  return (
    <div className={cn("flex gap-1.5", align === "start" ? "items-start" : "items-center")}>
      {children}
    </div>
  );
}

interface ModeSelectProps {
  value: SourceMode;
  modes: SourceMode[];
  onChange(m: SourceMode): void;
  disabled?: boolean;
}

function ModeSelect({ value, modes, onChange, disabled }: ModeSelectProps) {
  return (
    <select
      disabled={disabled}
      value={value}
      onChange={(e) => onChange(e.target.value as SourceMode)}
      title="Como o valor é resolvido em runtime"
      className="shrink-0 px-1.5 py-1.5 text-[10px] border border-border rounded bg-surface-subtle text-muted-foreground font-mono-tight cursor-pointer hover:bg-surface-2 disabled:cursor-not-allowed"
      style={{ width: "5.5rem" }}
    >
      {modes.map((m) => (
        <option key={m} value={m}>
          {MODE_LABELS[m]}
        </option>
      ))}
    </select>
  );
}
